import { useMemo, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { api, ApiError, type AdminDevice } from '../api';
import { useAdminDevices } from '../hooks';
import { useMe, useUsers } from '../../../hooks';
import { formatImported, timeAgo } from '../../../shared/format';
import { canViewInternal, isManager } from '../../../shared/persona';

const PHASE_LABEL: Record<string, { text: string; className: string }> = {
  live: { text: '实时', className: 'ok' },
  stale: { text: '陈旧', className: 'warn' },
  idle: { text: '空闲', className: '' },
  offline: { text: '离线', className: 'danger' },
};

const GRANT_HOURS = [
  { value: 1, label: '1 小时' },
  { value: 8, label: '8 小时' },
  { value: 24, label: '24 小时' },
  { value: 72, label: '3 天' },
  { value: 168, label: '7 天' },
  { value: 720, label: '30 天' },
];
const DEFAULT_GRANT_HOURS = 8;

export default function Devices() {
  const me = useMe();
  const isAdmin = isManager(me.data?.persona ?? 'visitor');
  const devices = useAdminDevices(isAdmin);
  const users = useUsers(isAdmin);
  const queryClient = useQueryClient();

  const [form, setForm] = useState({ id: 'ccs-lab-01', name: 'CCS 实验台', site: '上海实验室' });
  const [secret, setSecret] = useState<{ deviceId: string; token: string } | null>(null);
  const [notice, setNotice] = useState<{ kind: 'ok' | 'err'; text: string } | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [grantChoice, setGrantChoice] = useState<Record<string, string>>({});
  const [grantHours, setGrantHours] = useState<Record<string, number>>({});
  const [copied, setCopied] = useState(false);

  const userMap = useMemo(
    () => new Map((users.data ?? []).map((user) => [user.id, user])),
    [users.data],
  );

  const refresh = () => queryClient.invalidateQueries({ queryKey: ['admin-devices'] });

  const create = async (event: FormEvent) => {
    event.preventDefault();
    setBusy('create');
    setNotice(null);
    setSecret(null);
    try {
      const created = await api<{ device: { id: string }; token: string }>('/api/admin/devices', {
        method: 'POST',
        body: JSON.stringify({
          id: form.id.trim(),
          name: form.name.trim(),
          site: form.site.trim() || null,
        }),
      });
      setSecret({ deviceId: created.device.id, token: created.token });
      setNotice({ kind: 'ok', text: `设备 ${created.device.id} 已创建` });
      await refresh();
    } catch (err) {
      const code = err instanceof ApiError ? err.code : '';
      setNotice({
        kind: 'err',
        text:
          code === 'device_exists'
            ? '设备 ID 已存在'
            : code === 'invalid_request'
              ? '请检查设备 ID（3–40 位小写字母、数字、连字符）'
              : '创建失败',
      });
    } finally {
      setBusy(null);
    }
  };

  const patch = async (device: AdminDevice, body: Record<string, unknown>, success: string) => {
    setBusy(device.id);
    setNotice(null);
    try {
      await api(`/api/admin/devices/${encodeURIComponent(device.id)}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      });
      setNotice({ kind: 'ok', text: success });
      await refresh();
    } catch {
      setNotice({ kind: 'err', text: '操作失败' });
    } finally {
      setBusy(null);
    }
  };

  const rotate = async (device: AdminDevice) => {
    if (
      !window.confirm(
        `轮换 ${device.name} 的令牌？旧令牌会立即失效，工控机需要更新配置并重启桥接。`,
      )
    ) {
      return;
    }
    setBusy(device.id);
    setNotice(null);
    try {
      const result = await api<{ token: string }>(
        `/api/admin/devices/${encodeURIComponent(device.id)}/token`,
        { method: 'POST', body: JSON.stringify({}) },
      );
      setSecret({ deviceId: device.id, token: result.token });
      setNotice({ kind: 'ok', text: '令牌已轮换；旧令牌已失效' });
      await refresh();
    } catch {
      setNotice({ kind: 'err', text: '轮换失败' });
    } finally {
      setBusy(null);
    }
  };

  const grant = async (device: AdminDevice) => {
    const value = grantChoice[device.id];
    const userId = Number(value);
    if (!userId) return;
    const hours = grantHours[device.id] ?? DEFAULT_GRANT_HOURS;
    const expiresAt = new Date(Date.now() + hours * 3_600_000).toISOString();
    setBusy(device.id);
    setNotice(null);
    try {
      await api(`/api/admin/devices/${encodeURIComponent(device.id)}/grants`, {
        method: 'POST',
        body: JSON.stringify({ userId, expiresAt }),
      });
      setNotice({
        kind: 'ok',
        text: `已授权 ${userMap.get(userId)?.display_name ?? userId} 控制 ${device.name}，至 ${formatImported(expiresAt)}`,
      });
      setGrantChoice((prev) => ({ ...prev, [device.id]: '' }));
      await refresh();
    } catch (err) {
      const code = err instanceof ApiError ? err.code : '';
      setNotice({
        kind: 'err',
        text:
          code === 'grant_too_long'
            ? '授权时长不能超过 30 天'
            : code === 'invalid_expires_at'
              ? '授权到期时间必须晚于现在'
              : code === 'target_not_internal'
                ? '只能授权给内部成员'
                : '授权失败',
      });
    } finally {
      setBusy(null);
    }
  };

  const revoke = async (device: AdminDevice, userId: number) => {
    setBusy(device.id);
    setNotice(null);
    try {
      await api(
        `/api/admin/devices/${encodeURIComponent(device.id)}/grants/${userId}`,
        { method: 'DELETE' },
      );
      setNotice({
        kind: 'ok',
        text: `已撤销 ${userMap.get(userId)?.display_name ?? userId} 的控制权限`,
      });
      await refresh();
    } catch {
      setNotice({ kind: 'err', text: '撤销失败' });
    } finally {
      setBusy(null);
    }
  };

  const copyToken = async () => {
    if (!secret) return;
    try {
      await navigator.clipboard.writeText(secret.token);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      // http 环境或浏览器不允许剪贴板时，用户仍可手动选中
    }
  };

  if (!isAdmin) {
    return (
      <div className="empty">
        <b>仅管理员/组内教师可见</b>
        当前账号没有设备管理权限。
      </div>
    );
  }

  return (
    <>
      <div className="page-head">
        <div>
          <div className="eyebrow">Admin · Bridge</div>
          <h1>设备与令牌</h1>
        </div>
        <span className="badge">{devices.data?.length ?? 0} 台设备</span>
      </div>

      {notice ? <div className={`notice ${notice.kind}`}>{notice.text}</div> : null}

      {secret ? (
        <section className="panel device-token">
          <div>
            <div className="eyebrow">只显示这一次</div>
            <h2>{secret.deviceId} · 设备令牌</h2>
            <p className="muted">
              写入工控机 <code>bridge.config.json</code> 的 <code>server.token</code>，或使用
              <code>install.ps1 -Token</code> 让脚本做 DPAPI 加密。离开本页后无法再次查看。
            </p>
            <code className="device-token-value">{secret.token}</code>
          </div>
          <button type="button" className="btn btn-primary btn-sm" onClick={copyToken}>
            {copied ? '已复制' : '复制令牌'}
          </button>
        </section>
      ) : null}

      <section className="panel device-create">
        <div className="section-head">
          <h2>新增设备</h2>
          <span className="muted" style={{ fontSize: 11 }}>
            ID 只允许小写字母、数字和连字符
          </span>
        </div>
        <form className="device-create-grid" onSubmit={create}>
          <label>
            设备 ID
            <input
              className="input"
              value={form.id}
              onChange={(event) => setForm({ ...form, id: event.target.value })}
              placeholder="ccs-lab-01"
              required
            />
          </label>
          <label>
            名称
            <input
              className="input"
              value={form.name}
              onChange={(event) => setForm({ ...form, name: event.target.value })}
              placeholder="CCS 实验台"
              required
            />
          </label>
          <label>
            现场
            <input
              className="input"
              value={form.site}
              onChange={(event) => setForm({ ...form, site: event.target.value })}
              placeholder="上海实验室"
            />
          </label>
          <button type="submit" className="btn btn-primary" disabled={busy === 'create'}>
            {busy === 'create' ? '创建中…' : '创建并生成令牌'}
          </button>
        </form>
      </section>

      {devices.isLoading ? (
        <div className="empty">加载设备…</div>
      ) : devices.data?.length ? (
        <section className="panel table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>设备</th>
                <th>状态</th>
                <th>控制</th>
                <th>授权（到期自动失效）</th>
                <th>操作</th>
              </tr>
            </thead>
            <tbody>
              {devices.data.map((device) => {
                const phase = PHASE_LABEL[device.phase] ?? PHASE_LABEL.offline;
                const candidates = (users.data ?? []).filter(
                  (user) =>
                    canViewInternal(user.persona) &&
                    !user.is_read_only &&
                    !device.grants.some((grant) => grant.userId === user.id),
                );
                return (
                  <tr key={device.id}>
                    <td>
                      <b>{device.name}</b>
                      <div className="mono muted" style={{ fontSize: 11 }}>
                        {device.id}
                        {device.site ? ` · ${device.site}` : ''}
                      </div>
                      <div className="muted" style={{ fontSize: 11 }}>
                        {device.bridgeVersion ?? '未连接过'} ·{' '}
                        {device.lastSeenAt ? timeAgo(device.lastSeenAt) : '从未上线'}
                      </div>
                    </td>
                    <td>
                      <span className={`badge ${phase.className}`}>{phase.text}</span>
                      {!device.enabled ? <span className="badge danger">已停用</span> : null}
                    </td>
                    <td>
                      <button
                        type="button"
                        className={`btn btn-sm ${device.controlEnabled ? '' : 'btn-ghost'}`}
                        disabled={busy === device.id}
                        onClick={() =>
                          patch(
                            device,
                            { controlEnabled: !device.controlEnabled },
                            device.controlEnabled ? '已关闭远程控制' : '已开启远程控制',
                          )
                        }
                      >
                        {device.controlEnabled ? '可控制' : '已禁用'}
                      </button>
                    </td>
                    <td>
                      <div className="device-grants">
                        {device.grants.length ? (
                          device.grants.map((grant) => (
                            <span className="device-chip" key={grant.userId}>
                              {userMap.get(grant.userId)?.display_name ?? `用户 ${grant.userId}`}
                              <span className="device-chip-expiry">
                                至 {formatImported(grant.expiresAt)}
                              </span>
                              <button
                                type="button"
                                aria-label="撤销控制权限"
                                disabled={busy === device.id}
                                onClick={() => revoke(device, grant.userId)}
                              >
                                ×
                              </button>
                            </span>
                          ))
                        ) : (
                          <span className="muted" style={{ fontSize: 11 }}>
                            管理员默认可控
                          </span>
                        )}
                      </div>
                      <div className="device-grant-add">
                        {users.isLoading ? (
                          <span className="muted" style={{ fontSize: 11 }}>
                            加载账号…
                          </span>
                        ) : candidates.length ? (
                          <>
                            <select
                              className="input"
                              value={grantChoice[device.id] ?? ''}
                              onChange={(event) =>
                                setGrantChoice((prev) => ({
                                  ...prev,
                                  [device.id]: event.target.value,
                                }))
                              }
                            >
                              <option value="">选择账号…</option>
                              {candidates.map((user) => (
                                <option key={user.id} value={user.id}>
                                  {user.display_name}（{user.username}）
                                </option>
                              ))}
                            </select>
                            <select
                              className="input device-grant-duration"
                              aria-label="授权时长"
                              value={grantHours[device.id] ?? DEFAULT_GRANT_HOURS}
                              onChange={(event) =>
                                setGrantHours((prev) => ({
                                  ...prev,
                                  [device.id]: Number(event.target.value),
                                }))
                              }
                            >
                              {GRANT_HOURS.map((option) => (
                                <option key={option.value} value={option.value}>
                                  {option.label}
                                </option>
                              ))}
                            </select>
                            <button
                              type="button"
                              className="btn btn-ghost btn-sm"
                              disabled={!grantChoice[device.id] || busy === device.id}
                              onClick={() => grant(device)}
                            >
                              授权
                            </button>
                          </>
                        ) : (
                          <span className="muted" style={{ fontSize: 11, lineHeight: 1.6 }}>
                            没有可授权账号（管理员默认可控）。
                            <Link to="/admin/users">先创建账号 →</Link>
                          </span>
                        )}
                      </div>
                    </td>
                    <td>
                      <div className="device-actions">
                        <button
                          type="button"
                          className="btn btn-ghost btn-sm"
                          disabled={busy === device.id}
                          onClick={() => rotate(device)}
                        >
                          轮换令牌
                        </button>
                        <button
                          type="button"
                          className="btn btn-ghost btn-sm"
                          disabled={busy === device.id}
                          onClick={() =>
                            patch(
                              device,
                              { enabled: !device.enabled },
                              device.enabled ? '设备已停用' : '设备已启用',
                            )
                          }
                        >
                          {device.enabled ? '停用' : '启用'}
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </section>
      ) : (
        <div className="empty">
          <b>还没有设备</b>
          先创建一台设备并把令牌写入工控机，桥接上线后这里会显示心跳与状态。
        </div>
      )}

      <section className="panel device-help">
        <h2>工控机接入</h2>
        <p className="muted">
          本页只负责登记与授权。实际的 NI 采集、串口和本地落盘由 `apps/bridge` 的 CcsBridge
          服务完成；服务通过出站 WSS 连接，不需要在工控机开放入站端口。
        </p>
        <p className="muted">
          管理员默认可控；其他账号的授权必须选择到期时间（最长 30 天），到期后自动失效。
        </p>
        <p className="muted">设备列表每 15 秒自动刷新。</p>
      </section>
    </>
  );
}